import { H1, Subtitle1 } from 'ui/theme';
import { Tooltip } from 'ui/tooltip/Tooltip';
import { FocusStyle, TipPlacement } from 'ui/tooltip/types';
import { Help24 } from 'ui/icons/aurora/24px/Help24';
import { Refresh24 } from 'ui/icons/aurora/24px/Refresh24';
import { BUTTON_VARIANTS, IconButton } from 'ui/button';
import { AnonUserCriteria } from 'modules/graphql/types/GraphQLAnonymousCriteria';
import { Avatar } from 'ui/avatar';
import { displayDateWithDayWeekMonthYearPrefix } from 'utils/date';
import { localizeDateTime } from 'utils/l10n';
import { formatNumberGivenValueType } from 'modules/productAnalytics/utils';
import { NUMBER } from 'ui/card/constants';
import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';
import { MODERNIZED_SEGMENTATION_PAGE } from 'modules/segmentation/constants';
import { SegmentationLinkButton } from 'modules/segmentation/navigation/SegmentationLinkButton';

import {
  CreatedDetails,
  CreatedSectionHeader,
  CriteriaDetailsDescription,
  CriteriaDetailsSidebarContainer,
  SidebarRow,
  StyledDivider,
  StyledLinkButton,
  TooltipContent,
} from './AnonymousUserTracking.styled';
import { CONTENT } from '../constants';
import { useQueryAnonUserCount } from '../hooks/useQueryAnonUserCount';
import { createSegmentationSearchQuery } from './AnonymousCriteriaTable/utils';

export const CriteriaDetailsSidebar = ({
  criteria,
  isMobileView,
}: {
  criteria: AnonUserCriteria;
  isMobileView: boolean;
}) => {
  const {
    count,
    refetch: refetchUserCount,
    isLoading: userCountLoading,
  } = useQueryAnonUserCount(criteria.id);
  const totalAnonUsersAdded = formatNumberGivenValueType(NUMBER, count);
  const createdDateRelative =
    criteria?.createdAt && displayDateWithDayWeekMonthYearPrefix(new Date(criteria.createdAt));
  const createdDateFormatted =
    criteria?.createdAt && localizeDateTime(new Date(criteria.createdAt), { showTime: true });
  const isModernizedSegmentationPageEnabled = useFeatureFlag(MODERNIZED_SEGMENTATION_PAGE);
  const segmentationSearchQuery = createSegmentationSearchQuery(criteria.id);

  return (
    <CriteriaDetailsSidebarContainer data-mobile-view={isMobileView}>
      <SidebarRow>
        <Subtitle1>Total unknown users added</Subtitle1>
        <Tooltip
          tooltipContent={CONTENT.criteriaDetailsPage.totalUserCountTooltipContent}
          $focusStyle={FocusStyle.Round}
        >
          <Help24 />
        </Tooltip>
      </SidebarRow>
      <SidebarRow>
        <H1>{totalAnonUsersAdded}</H1>
        <IconButton
          variant={BUTTON_VARIANTS.GHOST}
          onClick={() => refetchUserCount()}
          isLoading={userCountLoading}
          isDisabled={userCountLoading}
        >
          <Refresh24 height={24} width={24} />
        </IconButton>
      </SidebarRow>
      <CriteriaDetailsDescription>{criteria?.fields.description}</CriteriaDetailsDescription>
      {isModernizedSegmentationPageEnabled ? (
        <SegmentationLinkButton searchQuery={segmentationSearchQuery}>
          {CONTENT.criteriaDetailsPage.segmentationButtonLabel}
        </SegmentationLinkButton>
      ) : (
        <StyledLinkButton to={`/segmentation?anonCriteriaId=${criteria?.id}`} isAngular>
          {CONTENT.criteriaDetailsPage.segmentationButtonLabel}
        </StyledLinkButton>
      )}
      <StyledDivider />
      <CreatedSectionHeader>Created</CreatedSectionHeader>
      <SidebarRow>
        <Tooltip
          isEnabled={!!criteria?.creatorUser?.fullName}
          placement={TipPlacement.Bottom}
          tooltipContent={criteria?.creatorUser?.fullName}
        >
          <Avatar
            firstName={criteria?.creatorUser?.fullName || ''}
            imgSrc={criteria?.creatorUser?.avatarUrl}
          />
        </Tooltip>
        <Tooltip
          placement={TipPlacement.Bottom}
          isUnderlined
          isUnderlinedGrey
          tooltipContent={
            <TooltipContent>
              <div>{createdDateFormatted}</div>
              {criteria?.creatorUser?.fullName && <div>by {criteria?.creatorUser?.fullName}</div>}
            </TooltipContent>
          }
        >
          <CreatedDetails>{createdDateRelative}</CreatedDetails>
        </Tooltip>
      </SidebarRow>
    </CriteriaDetailsSidebarContainer>
  );
};
